import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import {
  EventApplication,
  ResidentCaliber,
  getEventUserFullName,
  getResidentDisplayName,
  useAddEventApplicationMutation,
  useCopyEventApplicationsMutation,
  useGetEventApplicationsQuery,
  useGetEventsQuery,
  useRemoveEventApplicationMutation,
} from "../../../entities/admin-events";
import {
  CommunityUser,
  useGetCommunityUsersQuery,
} from "../../../entities/admin-communities";

interface EventApplicationsManagementProps {
  eventId: number;
  /** Срок окончания регистрации: после него бэкенд не даёт менять состав */
  registrationEndAt?: string;
}

/** Сколько последних мероприятий предлагать в качестве источника для копирования */
const COPY_SOURCE_EVENTS_LIMIT = 50;

const roleLabel = (role: CommunityUser["role"]) => {
  switch (role) {
    case "resident":
      return "Резидент";
    case "guest":
      return "Гость";
    default:
      return "Пользователь";
  }
};

const roleColor = (role: CommunityUser["role"]) => {
  switch (role) {
    case "resident":
      return "primary" as const;
    case "guest":
      return "secondary" as const;
    default:
      return "default" as const;
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy HH:mm");
  } catch {
    return "—";
  }
};

/**
 * Достаёт текст ошибки из ответа бэкенда. Отдельно разбирается
 * noEventRegistration — он приходит, когда срок регистрации уже истёк, и общая
 * фраза «не удалось» тут ничего не объясняет.
 */
const getErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { data?: { code?: string; message?: string } })?.data;

  if (data?.code === "noEventRegistration") {
    return "Срок регистрации на мероприятие истёк — состав участников больше нельзя менять.";
  }

  if (data?.code === "eventParticipantsCopySameEvent") {
    return "Нельзя скопировать состав мероприятия в него же — выберите другое мероприятие.";
  }

  return data?.message || fallback;
};

export const EventApplicationsManagement = ({
  eventId,
  registrationEndAt,
}: EventApplicationsManagementProps) => {
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<CommunityUser | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceEventId, setCopySourceEventId] = useState<number | "">("");
  const [feedback, setFeedback] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const [applicationToRemove, setApplicationToRemove] =
    useState<EventApplication | null>(null);

  const {
    data: applicationsData,
    isLoading: isApplicationsLoading,
    isFetching: isApplicationsFetching,
  } = useGetEventApplicationsQuery(eventId, { skip: !eventId });

  const applications = useMemo(
    () => applicationsData?.applications ?? [],
    [applicationsData],
  );

  const alreadyAddedIds = useMemo(
    () => new Set(applications.map((application) => application.user.id)),
    [applications],
  );

  const { data: usersData, isFetching: isUsersFetching } =
    useGetCommunityUsersQuery(
      { search: userSearch, limit: 20 },
      { skip: userSearch.trim().length < 2 },
    );

  const { data: copySourceEventsData, isFetching: isCopySourceEventsFetching } =
    useGetEventsQuery(
      { sort: "startat", order: "desc", limit: COPY_SOURCE_EVENTS_LIMIT },
      { skip: !copyDialogOpen },
    );

  const [addApplication, { isLoading: isAdding }] =
    useAddEventApplicationMutation();
  const [removeApplication, { isLoading: isRemoving }] =
    useRemoveEventApplicationMutation();
  const [copyApplications, { isLoading: isCopying }] =
    useCopyEventApplicationsMutation();

  // Бэкенд запрещает менять состав после окончания регистрации, гасим действия
  // заранее, чтобы админ не ловил ошибку на каждый клик.
  const isRegistrationClosed = registrationEndAt
    ? new Date(registrationEndAt).getTime() < Date.now()
    : false;

  const userOptions = (usersData?.users ?? []).filter(
    (user) => !alreadyAddedIds.has(user.id),
  );

  const copySourceEvents = (copySourceEventsData?.events ?? []).filter(
    (event) => event.id !== eventId,
  );

  const handleAdd = async () => {
    if (!selectedUser) return;

    try {
      await addApplication({ id: eventId, userId: selectedUser.id }).unwrap();
      setFeedback({
        severity: "success",
        text: `${selectedUser.firstName} ${selectedUser.lastName} добавлен(а) в состав участников.`.trim(),
      });
      setSelectedUser(null);
      setUserSearch("");
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Не удалось добавить участника."),
      });
    }
  };

  const handleRemove = async () => {
    if (!applicationToRemove) return;

    const { user } = applicationToRemove;

    try {
      await removeApplication({ id: eventId, userId: user.id }).unwrap();
      setFeedback({
        severity: "success",
        text: `${getEventUserFullName(user)} исключён(а) из состава участников.`,
      });
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Не удалось исключить участника."),
      });
    } finally {
      setApplicationToRemove(null);
    }
  };

  const handleCopy = async () => {
    if (!copySourceEventId) return;

    try {
      const { copiedCount } = await copyApplications({
        id: eventId,
        sourceEventId: Number(copySourceEventId),
      }).unwrap();
      setFeedback({
        severity: "success",
        text:
          copiedCount > 0
            ? `Добавлено участников: ${copiedCount}. Проверьте список и при необходимости скорректируйте состав.`
            : "Новых участников не добавилось — все они уже были в составе.",
      });
      setCopyDialogOpen(false);
      setCopySourceEventId("");
    } catch (error) {
      setFeedback({
        severity: "error",
        text: getErrorMessage(error, "Не удалось скопировать состав участников."),
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6">
          Состав участников
          {applications.length > 0 ? ` (${applications.length})` : ""}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => setCopyDialogOpen(true)}
          disabled={isRegistrationClosed}
        >
          Скопировать из мероприятия
        </Button>
      </Box>

      {isRegistrationClosed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Срок регистрации истёк
          {registrationEndAt ? ` ${formatDateTime(registrationEndAt)}` : ""} —
          состав участников больше нельзя менять.
        </Alert>
      )}

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.text}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
        <Autocomplete
          sx={{ flex: 1 }}
          options={userOptions}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          inputValue={userSearch}
          onInputChange={(_, value) => setUserSearch(value)}
          loading={isUsersFetching}
          disabled={isRegistrationClosed}
          filterOptions={(options) => options}
          getOptionLabel={(user) =>
            `${user.firstName} ${user.lastName} · ${user.phoneNumber}`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={
            userSearch.trim().length < 2
              ? "Введите минимум 2 символа"
              : "Никого не найдено"
          }
          renderOption={(props, user) => (
            <Box component="li" {...props} key={user.id}>
              <Avatar
                src={user.avatarUrl}
                sx={{ width: 28, height: 28, mr: 1 }}
              >
                {user.firstName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2">
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.phoneNumber} · {roleLabel(user.role)}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Найти пользователя по имени или телефону"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isUsersFetching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAdd}
          disabled={!selectedUser || isAdding || isRegistrationClosed}
          sx={{ height: 56 }}
        >
          Добавить
        </Button>
      </Stack>

      {isApplicationsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Участник</TableCell>
                <TableCell>Телефон</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Записан</TableCell>
                <TableCell>Дата записи</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Состав участников пуст
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application) => {
                  const { user } = application;
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={user.avatarUrl ?? undefined}
                            sx={{ width: 32, height: 32 }}
                          >
                            {user.firstName?.charAt(0) ?? "?"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {getEventUserFullName(user) || "Без имени"}
                            </Typography>
                            {user.caliber && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {getResidentDisplayName(
                                  user.caliber as ResidentCaliber,
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{user.phoneNumber || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={roleLabel(user.role)}
                          color={roleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            application.registeredBy
                              ? "Администратором"
                              : "Самостоятельно"
                          }
                          color={application.registeredBy ? "info" : "default"}
                          variant="outlined"
                          size="small"
                          title={
                            getEventUserFullName(application.registeredBy) ||
                            undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatDateTime(application.registeredAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          color="error"
                          size="small"
                          startIcon={<PersonRemoveIcon />}
                          onClick={() => setApplicationToRemove(application)}
                          disabled={isRemoving || isRegistrationClosed}
                        >
                          Исключить
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {isApplicationsFetching && !isApplicationsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </TableContainer>
      )}

      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Скопировать состав участников</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Участники выбранного мероприятия будут записаны на текущее. Уже
            записанные останутся на месте — после копирования состав можно
            отредактировать вручную.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Мероприятие-источник</InputLabel>
            <Select
              value={copySourceEventId}
              label="Мероприятие-источник"
              onChange={(e) => setCopySourceEventId(Number(e.target.value))}
            >
              {isCopySourceEventsFetching && (
                <MenuItem disabled value="">
                  Загрузка...
                </MenuItem>
              )}
              {copySourceEvents.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.title} · {formatDateTime(event.startAt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleCopy}
            disabled={!copySourceEventId || isCopying}
          >
            Скопировать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(applicationToRemove)}
        onClose={() => setApplicationToRemove(null)}
      >
        <DialogTitle>Исключить участника</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Исключить {getEventUserFullName(applicationToRemove?.user)} из
            состава участников?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationToRemove(null)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={isRemoving}
          >
            Исключить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
